import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  useTheme,
  CircularProgress,
  Alert,
  Chip,
  Divider
} from '@mui/material';
import { 
  SportsEsports, 
  CalendarToday, 
  LocationOn,
  ArrowBack as ArrowBackIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import MKBox from '../components/MKBox';
import MKTypography from '../components/MKTypography';
import MKButton from '../components/MKButton';
import { PRIMARY_PINK, PRIMARY_PINK_DARK } from '../constants';
import API_BASE_URL from '../apiConfig';
import SEOHead from '../components/SEO/SEOHead';
import StructuredData from '../components/SEO/StructuredData';

function MajorLandingPage() {
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const [major, setMajor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const tournamentColors = [
    "#ff6b35", // Orange
    "#4ecdc4", // Teal
    "#45b7d1", // Blue
    "#f9ca24", // Yellow
    "#a8e6cf", // Green
    "#ffd3a5", // Peach
    "#fd79a8", // Pink
    "#6c5ce7"  // Purple
  ];

  useEffect(() => {
    fetchMajor();
  }, [id]);

  const fetchMajor = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(`${API_BASE_URL}/api/majors/${id}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Major not found');
        }
        throw new Error('Failed to fetch major');
      }

      const majorData = await response.json();
      setMajor(majorData);
    } catch (error) {
      console.error('Error fetching major:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "TBD";
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    // Ensure we're working with a proper Date object
    // If the string doesn't have timezone info, assume it's UTC
    let date;
    if (typeof dateString === 'string' && !dateString.includes('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
      // If no timezone indicator, assume it's UTC and append 'Z'
      date = new Date(dateString.endsWith('Z') ? dateString : dateString + 'Z');
    } else {
      date = new Date(dateString);
    }
    
    // Use the browser's local timezone for display
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    });
  };

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: "100vh", 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center' 
      }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: "100vh", py: 8 }}>
        <Container maxWidth="lg">
          <Alert severity="error" sx={{ textAlign: 'center' }}>
            {error}
          </Alert>
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <MKButton
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={handleBackClick}
              sx={{
                backgroundColor: PRIMARY_PINK,
                color: 'white !important',
                '&:hover': {
                  backgroundColor: PRIMARY_PINK_DARK,
                  color: 'white !important',
                },
              }}
            >
              Go Back
            </MKButton>
          </Box>
        </Container>
      </Box>
    );
  }

  if (!major) {
    return null;
  }

  return (
    <Box sx={{ minHeight: "100vh", pt: 0, pb: 0 }}>
      <SEOHead 
        title={`${major.title} - Major Event`}
        description={`Join ${major.title} featuring ${major.events?.length || 0} tournaments. ${major.content || ''}`}
        keywords={`${major.title}, major event, tournament, gaming competition, esports`}
        url={`https://sandiegogaming.com/majors/${major.id}`}
      />
      <StructuredData 
        type="event" 
        data={{
          name: major.title,
          description: major.content || `Major event featuring ${major.events?.length || 0} tournaments`,
          startDate: major.start_date,
          endDate: major.end_date,
          location: "Various Locations",
          price: "0"
        }}
      />
      
      <Container maxWidth="lg">
        {/* Back Button */}
        <Box sx={{ mb: 3, mt: 2 }}>
          <MKButton
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            sx={{
              borderColor: PRIMARY_PINK,
              color: PRIMARY_PINK,
              '&:hover': {
                borderColor: PRIMARY_PINK_DARK,
                backgroundColor: PRIMARY_PINK_DARK,
                color: 'white !important'
              },
            }}
          >
            Back
          </MKButton>
        </Box>

        {/* Major Banner */}
        {major.banner_url && (
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Card
              sx={{
                maxWidth: 800,
                mx: "auto",
                pb: 0,
                overflow: "hidden",
                borderRadius: 3,
                boxShadow: theme.palette.mode === 'dark' 
                  ? '0 8px 32px rgba(0,0,0,0.3)' 
                  : '0 8px 32px rgba(0,0,0,0.1)'
              }}
            >
                <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                  <img
                    src={major.banner_url}
                    alt={`${major.title} banner`}
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block'
                    }}
                    loading="lazy"
                    onError={(e) => {
                      console.error('Failed to load banner image:', major.banner_url);
                      e.target.style.display = 'none';
                    }}
                    onLoad={() => {
                      console.log('Banner image loaded successfully:', major.banner_url);
                    }}
                  />
                </CardContent>
            </Card>
          </Box>
        )}

        {/* Major Title */}
        {major.show_title && (
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <MKTypography
              variant="h2"
              fontWeight="bold"
              mb={2}
              sx={{ 
                color: theme.palette.mode === 'dark' ? 'white !important' : '#333333 !important'
              }}
            >
              {major.title}
            </MKTypography>
          </Box>
        )}

        {/* Major Info */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Grid container spacing={2} justifyContent="center">
            <Grid item>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CalendarToday sx={{ mr: 1, color: PRIMARY_PINK }} />
                <MKTypography variant="body1" fontWeight="bold" color="text.secondary">
                  {formatDateTime(major.start_date)} - {formatDateTime(major.end_date)}
                </MKTypography>
              </Box>
            </Grid>
            {major.events && major.events.length > 0 && (
              <Grid item>
                <Chip
                  label={`${major.events.length} Tournament${major.events.length > 1 ? 's' : ''}`}
                  sx={{
                    backgroundColor: PRIMARY_PINK,
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                />
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Additional Content */}
        {major.content && (
          <Box sx={{ mb: 8 }}>
            <Card
              sx={{
                maxWidth: 800,
                mx: "auto",
                background: theme.palette.mode === 'dark' 
                  ? 'linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%)'
                  : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                border: theme.palette.mode === 'dark' 
                  ? '1px solid #444'
                  : '1px solid #dee2e6',
                borderRadius: 3,
                boxShadow: theme.palette.mode === 'dark' 
                  ? '0 8px 32px rgba(0,0,0,0.3)' 
                  : '0 8px 32px rgba(0,0,0,0.1)'
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <MKTypography
                  variant="h4"
                  fontWeight="bold"
                  mb={3}
                  sx={{ 
                    textAlign: "center",
                    color: theme.palette.mode === 'dark' ? 'white !important' : '#333333 !important'
                  }}
                >
                  Event Information
                </MKTypography>
                <MKTypography
                  variant="body1"
                  sx={{ 
                    color: theme.palette.mode === 'dark' ? 'white !important' : '#333333 !important',
                    whiteSpace: 'pre-line',
                    lineHeight: 1.6
                  }}
                >
                  {major.content}
                </MKTypography>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Events Section */}
        {major.events && major.events.length > 0 && (
          <Box sx={{ mb: 8 }}>
            <MKTypography
              variant="h3"
              fontWeight="bold"
              mb={4}
              sx={{ 
                textAlign: "center",
                color: theme.palette.mode === 'dark' ? 'white !important' : '#333333 !important'
              }}
            >
              {major.events_header}
            </MKTypography>
            
            <Grid container spacing={4} justifyContent="center">
              {major.events.map((event, eventIndex) => (
                <Grid item xs={12} sm={6} md={3} key={event.id}>
                  <Card
                    sx={{
                      height: '100%',
                      background: theme.palette.mode === 'dark' 
                        ? 'linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%)'
                        : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                      border: `2px solid ${tournamentColors[eventIndex % tournamentColors.length]}`,
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 8px 25px ${tournamentColors[eventIndex % tournamentColors.length]}40`
                      }
                    }}
                    onClick={() => window.open(`/events/${event.id}`, '_blank')}
                  >
                    <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                      {/* Game Icon */}
                      <Box sx={{ textAlign: 'center', mb: 2 }}>
                        <SportsEsports 
                          sx={{ 
                            fontSize: 40, 
                            color: tournamentColors[eventIndex % tournamentColors.length],
                            mb: 1 
                          }} 
                        />
                      </Box>

                      {/* Event Name */}
                      <MKTypography
                        variant="h5"
                        fontWeight="bold"
                        mb={2}
                        sx={{ 
                          textAlign: 'center',
                          color: theme.palette.mode === 'dark' ? 'white !important' : '#333333 !important'
                        }}
                      >
                        {event.title}
                      </MKTypography>

                      {/* Event Details */}
                      <Box sx={{ mb: 3, flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <CalendarToday sx={{ fontSize: 16, mr: 1, color: tournamentColors[eventIndex % tournamentColors.length] }} />
                          <MKTypography variant="body2" color="text.secondary">
                            {formatDateTime(event.start_time)} at {formatTime(event.start_time)}
                          </MKTypography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <LocationOn sx={{ fontSize: 16, mr: 1, color: tournamentColors[eventIndex % tournamentColors.length] }} />
                          {event.location_url ? (
                            <MKTypography 
                              variant="body2" 
                              color="text.secondary"
                              component="a"
                              href={event.location_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ 
                                textDecoration: 'none',
                                color: tournamentColors[eventIndex % tournamentColors.length],
                                '&:hover': {
                                  textDecoration: 'underline'
                                }
                              }}
                            >
                              {event.location_display_name || event.location || 'TBD'}
                            </MKTypography>
                          ) : (
                            <MKTypography variant="body2" color="text.secondary">
                              {event.location || 'TBD'}
                            </MKTypography>
                          )}
                        </Box>
                        
                        {event.rsvp_enabled && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <GroupIcon sx={{ fontSize: 16, mr: 1, color: tournamentColors[eventIndex % tournamentColors.length] }} />
                            <MKTypography variant="body2" color="text.secondary">
                              {event.rsvp_count || 0} / {event.rsvp_limit || '∞'} RSVP{event.rsvp_limit ? ` (${Math.max(0, (event.rsvp_limit || 0) - (event.rsvp_count || 0))} available)` : ''}
                            </MKTypography>
                          </Box>
                        )}
                      </Box>

                      {/* View Details Button */}
                      <MKButton
                        variant="contained"
                        fullWidth
                        sx={{
                          backgroundColor: tournamentColors[eventIndex % tournamentColors.length],
                          color: 'white !important',
                          '&:hover': {
                            backgroundColor: tournamentColors[eventIndex % tournamentColors.length],
                            color: 'white !important',
                            opacity: 0.9
                          }
                        }}
                      >
                        View Details
                      </MKButton>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Container>
    </Box>
  );
}

export default MajorLandingPage;
